<script setup lang="tsx">
import MitigationMeasuresCard from './cards/MitigationMeasuresCard.vue'
import ImpactedAssetsCard from './cards/ImpactedAssetsCard.vue'
import IconButton from '../shared/IconButton.vue'
import PlusButton from '../widgets/PlusButton.vue'
import { computed, onMounted, ref, watch } from 'vue'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import RiskScoreBadge from './RiskScoreBadge.vue'
import AxisLevelBadge from './AxisLevelBadge.vue'
import { daysUntilDate, formatDate, getTreatmentText, hasPermission, hasPermissionOwn, isRowClick, truncate } from '@/services/utils'
import DueDateLabel from '../widgets/DueDateLabel.vue'
import useRiskColumns from './columns'
import RiskTreatmentSelect from './RiskTreatmentSelect.vue'
import AxisLevelSelect from './AxisLevelSelect.vue'
import { calcRiskScore, formatRiskScore, parseRiskScore, weight } from './shared'
import {
	RET_AcceptableResidualAchieved,
	RiskReference,
	RT_Exclude,
	RT_Reduce,
	RT_Preserve,
	WillNotApply,
	WillNotImplement,
	type MethodStatus,
	RT_Unscored,
	PermAllRiskWrite,
	PermOwnRiskWrite,
	PermAllRiskDelete,
	PermOwnRiskDelete,
} from '@/models/enums-generated'
import type { MitigationMeasure, Responsible, RiskMeta } from '@/models/api-generated'
import Datepicker from '@vuepic/vue-datepicker'
import { formatLocale, getDateFormat } from '@/services/utils'
import CommentReferencesContainer from '../comment_references/CommentReferencesContainer.vue'
import { riskConfig } from './riskConfig'
import { refreshOverviewCallback } from './overview/overviewShared'
import { useI18n } from 'vue-i18n'
import http from '@/services/http'
import { Range, type RangeMarks } from 'vue-range-multi'
import sliderStore from '@/stores/slider'
import RiskDecisions from './cards/RiskDecisions.vue'
import ConnectionCard from './cards/ConnectionCard.vue'
import { useTheme } from '../user/useTheme'

const { theme } = useTheme()
const { t } = useI18n()
const props = withDefaults(
	defineProps<{
		component?: string
		isEdit: boolean
		riskName: string
		isDeleted?: boolean
		onMetaDelete?: () => void
	}>(),
	{
		component: 'risks',
	},
)

const slider = sliderStore()
const model = defineModel<RiskMeta>({ required: true })
const { columns } = useRiskColumns(props.component)

const originalLastReview = ref<Date | null>(null)
const hasBeenUpdated = ref(false)
const originalResponsible = ref<Responsible | undefined>()

onMounted(() => {
	if (model.value) originalResponsible.value = model.value.responsible
})

const daysToDueDate = computed(() => {
	if (model.value.dueDate) {
		return daysUntilDate(model.value.dueDate)
	}
	return null
})

watch(() => model.value.impact, calculate)
watch(() => model.value.probability, calculate)

const manualResidualDisplay = computed({
	get: () =>
		model.value.manualResidual !== undefined ? Math.round(formatRiskScore(model.value.manualResidual)) : 0,
	set: (val: number) => {
		const frac = parseRiskScore(val)
		model.value.manualResidual = frac
		model.value.residual = frac
	},
})

function startManualResidual() {
	model.value.manualResidual = model.value.residual
}

function resetManualResidual() {
	model.value.manualResidual = undefined
	calculate()
}

function calculate() {
	if (model.value.manualResidual !== undefined) return
	if (model.value.probability === undefined || model.value.impact === undefined) return

	model.value.score = calcRiskScore(model.value.probability, model.value.impact)

	const measures = new Map<MethodStatus, MitigationMeasure[]>()
	let total = 0

	model.value.mitigationSummary.forEach((mod) =>
		mod.mitigationMeasures.forEach((m) => {
			if (!m.status || m.status === WillNotApply || m.status === WillNotImplement) return

			let arr = measures.get(m.status)
			if (!arr) {
				arr = []
				measures.set(m.status, arr)
			}
			arr.push(m)
			total++
		}),
	)

	if (total > 0) {
		let mitigation = 0
		measures.forEach((v, k) => {
			mitigation += (v.length / total) * weight[k]
		})
		model.value.residual = (1 - mitigation) * model.value.score
	} else {
		model.value.residual = model.value.score
	}

	if (model.value.treatment === RT_Unscored) {
		const acceptable = model.value.acceptedResidualRisk ?? riskConfig.value.acceptableRisk
		if (model.value.score > acceptable) {
			model.value.treatment = RT_Reduce
		} else {
			model.value.treatment = RT_Preserve
		}
	}
}

const isAssessed = computed(() => model.value.probability !== undefined && model.value.impact !== undefined)

const achievedAcceptedRisk = computed(() => {
	if (!isAssessed.value || model.value.treatment === RT_Exclude) return false
	const accepted = Number(model.value.acceptedResidualRisk ?? 0)
	const threshold = accepted > 0 ? accepted : Number(riskConfig.value.acceptableRisk ?? 0)

	// Whole scores, so this agrees with the displayed values and with the server's acceptable lists.
	return Math.round(formatRiskScore(Number(model.value.residual ?? 0))) <= Math.round(formatRiskScore(threshold))
})

const totalSummary = computed(() => model.value.mitigationSummary.find((item) => item.moduleName === 'total'))

const setCurrentTime = () => {
	if (!hasBeenUpdated.value) {
		originalLastReview.value = model.value.lastReview
		model.value.lastReview = new Date()
		hasBeenUpdated.value = true
	} else {
		model.value.lastReview = originalLastReview.value
		hasBeenUpdated.value = false
	}
}

function inputClass(reviewDate: string) {
	const today = new Date().toISOString()
	return { enabled: formatDate(reviewDate) === formatDate(today) }
}

const acceptableMarks = computed(() => {
	const marks: RangeMarks = {
		1: '1',
	}

	const f1 = Math.floor((riskConfig.value.matrixSize * riskConfig.value.matrixSize) / 2)
	marks[f1] = f1.toFixed()

	const f2 = riskConfig.value.matrixSize * riskConfig.value.matrixSize
	marks[f2] = f2.toFixed()

	return marks
})

const acceptableRiskRange = computed({
	get: () => {
		if (!model.value.acceptedResidualRisk) return formatRiskScore(riskConfig.value.acceptableRisk)
		return formatRiskScore(model.value.acceptedResidualRisk)
	},
	set: (s: number) => {
		const val = parseRiskScore(s)
		if (Math.abs(riskConfig.value.acceptableRisk - val) < 0.0001) model.value.acceptedResidualRisk = undefined
		else model.value.acceptedResidualRisk = val
	},
})

function openJournal() {
	slider.openJournal(props.riskName, [
		{ table: 'risks', rowId: model.value.id },
		{ table: 'risk_meta', rowId: model.value.id },
	])
}

function rowClick(e: MouseEvent) {
	if (!isRowClick(e)) return

	model.value.isExpanded = !model.value.isExpanded
}

const showTreatmentExclamation = computed(() => model.value.events.includes(RET_AcceptableResidualAchieved))
const acknowledgeTitle = computed(() => `${t('risk.acceptableAchievedEvent')} ${t('risk.acceptableAchievedAcknowledge')}`)

async function acknowledgeAcceptable() {
	const { status } = await http.post(`api/risk/meta/${model.value.id}/acknowledge`)
	if (status !== 200) return

	model.value.events = model.value.events.filter((e) => e !== RET_AcceptableResidualAchieved)
	refreshOverviewCallback.value?.()
}
const isExcluded = computed(() => model.value.treatment === RT_Exclude)

const isHighlighted = computed(() => {
	return showTreatmentExclamation.value
})

const totalImplementedCount = computed(
	() => (totalSummary.value?.moduleStatuses.i.count ?? 0) + (totalSummary.value?.moduleStatuses.ci.count ?? 0),
)

function onRiskDelete() {
	if (props.onMetaDelete) {
		props.onMetaDelete()
	}
}

const canEdit = computed(() => {
	return props.isEdit && canEditRiskFields.value
})

const canEditRiskFields = computed(() => {
	if (hasPermission(PermAllRiskWrite) || !model.value.id) return true

	const currentResponsible = model.value.responsible

	const canEditCurrent = hasPermissionOwn(currentResponsible, PermAllRiskWrite, PermOwnRiskWrite)
	const canEditOriginal = originalResponsible.value
		? hasPermissionOwn(originalResponsible.value, PermAllRiskWrite, PermOwnRiskWrite)
		: false

	return canEditCurrent || canEditOriginal
})

const canDeleteRisk = computed(() => {
	if (hasPermission(PermAllRiskWrite) || !model.value.id) return true

	const currentResponsible = model.value.responsible

	const canEditCurrent = hasPermissionOwn(currentResponsible, PermAllRiskDelete, PermOwnRiskDelete)
	const canEditOriginal = originalResponsible.value
		? hasPermissionOwn(originalResponsible.value, PermAllRiskDelete, PermOwnRiskDelete)
		: false

	return (canEditCurrent || canEditOriginal) && model.value.isCustom
})
</script>

<template>
	<tr :class="['risk', model.treatment, { 'highlight-row': isHighlighted, 'is-deleted': isDeleted }]" @click="rowClick">
		<td :data-test="'expand-risk-' + model.id" class="text-center">
			<PlusButton v-model="model.isExpanded"></PlusButton>
		</td>
		<td v-if="columns.connections.value.isVisible" class="text-center" v-one-tooltip="model.asset.name">
			<span class="badge bg-primary me-2 fixed-badge">
				{{ model.asset.name ? truncate(model.asset.name, 30) : $t('risk.default') }}
			</span>
		</td>

		<template v-if="!canEdit">
			<td v-if="columns.probability.value.isVisible">
				<AxisLevelBadge v-if="!isExcluded && model.probability !== undefined" :probability="model.probability" />
			</td>
			<td v-if="columns.impact.value.isVisible">
				<AxisLevelBadge v-if="!isExcluded && model.impact !== undefined" :impact="model.impact" />
			</td>
			<td v-if="columns.score.value.isVisible">
				<div v-if="!isExcluded && isAssessed" class="d-flex align-items-center">
					<i v-if="achievedAcceptedRisk" class="bi bi-droplet-half me-1" v-one-tooltip="$t('risk.acceptableAchieved')"></i>
					<RiskScoreBadge v-model="model.score" />
				</div>
			</td>
			<td v-if="columns.acceptedResidualRisk.value.isVisible">
				<div v-if="!isExcluded" class="d-flex justify-content-center">
					<b>{{ acceptableRiskRange.toFixed(0) }}</b>
					<i
						v-if="model.acceptedResidualRisk !== undefined"
						class="bi bi-asterisk text-muted smaller-text"
						v-one-tooltip="$t('risk.manualChange')"
					></i>
				</div>
			</td>
			<td v-if="columns.residual.value.isVisible">
				<template v-if="!isExcluded && isAssessed">
					<div class="d-flex align-items-center gap-1">
						<RiskScoreBadge v-model="model.residual" />
						<span
							v-if="model.manualResidual !== undefined"
							class="badge bg-warning text-dark smaller-text"
							v-one-tooltip="$t('risk.manualResidualTag')"
						>M</span>
					</div>
				</template>
			</td>
		</template>
		<template v-else>
			<td v-if="columns.probability.value.isVisible">
				<AxisLevelSelect
					:data-test="'risk-probability-input-' + model.id"
					class="w-100 input-box axis-compact"
					type="probability"
					v-model="model.probability"
				/>
			</td>
			<td v-if="columns.impact.value.isVisible">
				<AxisLevelSelect
					:data-test="'risk-impact-input-' + model.id"
					class="w-100 input-box axis-compact"
					type="impact"
					v-model="model.impact"
				/>
			</td>
			<td v-if="columns.score.value.isVisible">
				<RiskScoreBadge v-if="isAssessed" v-model="model.score" />
			</td>
			<td v-if="columns.acceptedResidualRisk.value.isVisible">
				<div class="text-nowrap d-flex justify-content-center">
					<b>{{ acceptableRiskRange.toFixed(0) }}</b>
					<i
						v-if="model.acceptedResidualRisk !== undefined"
						class="bi bi-asterisk text-muted smaller-text"
						v-one-tooltip="$t('risk.manualChange')"
					></i>
				</div>

				<div class="range-wrapper">
					<Range
						v-if="canEdit"
						class="p-2 mb-3 w-100"
						v-model="acceptableRiskRange"
						:min="1"
						:max="riskConfig.matrixSize * riskConfig.matrixSize"
						thumb-size="large"
						:show-stops="true"
						:marks="acceptableMarks"
					/>
				</div>
			</td>

			<td v-if="columns.residual.value.isVisible">
				<template v-if="!isExcluded && isAssessed">
					<div class="residual-wrapper d-flex align-items-center gap-1 flex-nowrap">
						<template v-if="model.manualResidual !== undefined">
							<input
								type="number"
								class="form-control form-control-sm manual-residual-input"
								v-model.number="manualResidualDisplay"
								:min="1"
								:max="riskConfig.matrixSize * riskConfig.matrixSize"
							/>
							<span class="badge bg-warning text-dark smaller-text" v-one-tooltip="$t('risk.manualResidualTag')">M</span>
							<IconButton
								class="bi bi-arrow-counterclockwise text-secondary"
								size="fs-6"
								:title="$t('risk.resetToAuto')"
								@click="resetManualResidual"
							/>
						</template>
						<template v-else>
							<RiskScoreBadge v-model="model.residual" />
							<IconButton
								class="bi bi-pencil text-secondary"
								size="fs-6"
								:title="$t('risk.evaluateManually')"
								@click="startManualResidual"
							/>
						</template>
					</div>
				</template>
			</td>
		</template>
		<td
			v-if="columns.mitigationMeasures.value.isVisible"
			v-one-tooltip="[$t('status.implemented'), $t('risk.mitigation.active'), $t('risk.mitigation.total')].join(' / ')"
			class="text-nowrap"
		>
			<span class="text-success fw-bold">
				{{ totalImplementedCount }}
			</span>
			/ {{ totalSummary?.applicableCount ?? 0 }} /
			{{ totalSummary?.mitigationMeasures.length ?? 0 }}
		</td>
		<template v-if="!canEdit">
			<td v-if="columns.responsible.value.isVisible">{{ model.responsible?.label }}</td>
			<td v-if="columns.treatment.value.isVisible" class="text-center">
				<template v-if="!showTreatmentExclamation">
					{{ getTreatmentText(model.treatment) }}
				</template>
				<IconButton
					v-else-if="canEditRiskFields"
					class="bi bi-exclamation-triangle"
					size="fs-5"
					hover-color="orange"
					:data-test="'risk-acknowledge-acceptable-' + model.id"
					:title="acknowledgeTitle"
					@click="acknowledgeAcceptable"
				/>
				<i v-else class="bi bi-exclamation-triangle fs-5" v-one-tooltip="$t('risk.acceptableAchievedEvent')"></i>
			</td>
			<td v-if="columns.dueDate.value.isVisible">
				<DueDateLabel :days-to-due-date="daysToDueDate"></DueDateLabel>
			</td>
			<td v-if="columns.lastReview.value.isVisible">
				<span>{{ model.lastReview ? formatDate(model.lastReview) : '-' }}</span>
			</td>
		</template>
		<template v-else>
			<td v-if="columns.responsible.value.isVisible">
				<ResponsibleSelect :data-test="'risk-responsible-input-' + model.id" class="w-100 input-box" v-model="model.responsible" />
			</td>
			<td v-if="columns.treatment.value.isVisible">
				<RiskTreatmentSelect :data-test="'risk-treatment-input-' + model.id" class="w-100" v-model="model.treatment" />
			</td>
			<td v-if="columns.dueDate.value.isVisible">
				<Datepicker
					:id="'risk-due-date' + model.id"
					v-model="model.dueDate"
					:data-test="'risk-duedate-input-' + model.id"
					:clearable="true"
					:teleport="true"
					:enable-time-picker="false"
					:format="getDateFormat()"
					:format-locale="formatLocale()"
					:select-text="$t('misc.select')"
					:cancel-text="$t('misc.cancel')"
					text-input
					class="input-box mb-1"
					:dark="theme === 'dark'"
				/>
			</td>
			<td v-if="columns.lastReview.value.isVisible">
				<button type="button" class="btn btn-outline-secondary" :class="inputClass(model.lastReview)" @click="setCurrentTime()">
					{{ model.lastReview ? formatDate(model.lastReview) : '-' }}
				</button>
			</td>
		</template>

		<td>
			<IconButton
				:id="'risk-history-btn_' + model.id"
				:data-test="'risk-history-' + model.id"
				class="bi bi-book"
				:title="$t('history.label')"
				size="fs-6"
				data-bs-toggle="tooltip"
				data-bs-placement="bottom"
				@mouseup="openJournal"
			>
			</IconButton>
		</td>
		<td v-if="isEdit" class="w-auto-32 text-center">
			<IconButton
				v-if="canDeleteRisk"
				:data-test="'risk-delete-' + model.id"
				class="bi bi-trash text-danger"
				@click="onRiskDelete"
				:title="$t('misc.delete')"
				size="fs-5"
			/>
		</td>
	</tr>

	<template v-if="model.isExpanded">
		<tr class="bordered-rows">
			<td colspan="100%">
				<CommentReferencesContainer
					v-if="model"
					v-model="model.comment"
					:canEdit="canEdit"
					:url-references="model.urlReferences"
					:document-references="model.documentReferences"
					:owner-id="model.id"
					:owner-type="RiskReference"
				>
					<template #before>
						<RiskDecisions v-model="model" />
						<div class="vr mx-3"></div>
					</template>
					<template #after v-if="model.asset.id">
						<div class="vr mx-3"></div>
						<ConnectionCard v-model="model" :is-edit="canEdit" />
					</template>
				</CommentReferencesContainer>
			</td>
		</tr>

		<tr class="bordered-rows">
			<td colspan="100%">
				<MitigationMeasuresCard
					v-model="model.mitigationSummary"
					:is-edit="canEdit"
					:risk-meta-id="model.id"
					:instance-id="model.instance.id"
					:asset-id="model.asset.id"
				></MitigationMeasuresCard>
			</td>
		</tr>
		<tr v-if="!model.asset.id" class="bordered-rows bottom-border">
			<td colspan="100%" class="pb-4">
				<ImpactedAssetsCard v-model="model.impactedAssets"></ImpactedAssetsCard>
			</td>
		</tr>
	</template>
</template>

<style lang="sass" scoped>
@import '~bootstrap/scss/functions'
@import '~bootstrap/scss/variables'

.blue td
  color: var(--bs-body-color)
  background-color: var(--bs-primary-bg-subtle)
  border-bottom-color: rgba(var(--bs-primary-rgb), .6)

tr.submethod > td
  cursor: pointer
  white-space: nowrap

tr.group-row > td
  cursor: default

td
  text-overflow: ellipsis

tr.is-changed > td
  background-color: #eeeeff
tr.is-deleted > td
  background-color: #ffeeee

.exclude
  border: 2px solid gray

  td
    background-color: silver

.highlight-row
  outline: 2px solid orange
  outline-offset: -2px

  :deep(.bi-exclamation-triangle)
    color: orange

.smaller-text
  font-size: 0.5rem

.range-wrapper
  min-width: 90px

.residual-wrapper
  width: 110px

.manual-residual-input
  width: 46px
  -moz-appearance: textfield
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button
    -webkit-appearance: none

:deep(.multiselect.is-invalid .multiselect__tags)
  border: 1px solid #dc3545
  border-radius: 0.25rem
</style>
